import { Popover } from 'antd';
import cl from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useSlateStatic } from 'slate-react';
import styled from 'styled-components';

import { colorsV2 } from '../../../../../colors-v2';
import ColorSelector, {
  ColorSelectionLightStyled,
} from '../../../../../DolEditor/elements/highlight/toolbar/ColorSelector';
import { BaseCardLogicProps } from '../../CardLogicPlugin.types';
import { CardLogicActionMenu } from '../../components/CardLogicActionMenu';
import { useCardLogicStore } from '../../model/card-logic.store';
import {
  CARD_LOGIC_HEADER_CELL_CLASS_NAME,
  CARD_LOGIC_HEADER_CELL_COLOR_SELECTION_CLASS_NAME,
  ELEMENT_BY_ID_ATTRIBUTES,
} from '../../model/constants';
import { CardLogicTransformActions } from '../../model/transforms';
import { CardLogicCellSharedStyle } from '../Cell/CardLogicCellElement';

interface CardLogicHeaderCellElementProps extends BaseCardLogicProps {}

const Main = styled.div<{ backgroundColor }>`
  ${CardLogicCellSharedStyle};
  color: ${colorsV2.black100};
  align-items: center;

  .cell-wrapper {
    &.text-align-center {
      text-align: center;
    }

    &.text-align-left {
      text-align: left;
    }

    &.text-align-right {
      text-align: right;
    }

    .card-logic-action-menu {
      top: 6px;
    }
  }

  .header-cell {
    > .header-wrapper {
      > * {
        background-color: ${(p) => p.backgroundColor};
        border-radius: 8px;
        padding: 2px 4px;
      }
    }
  }

  h3 {
    margin-bottom: 0;
  }

  .text {
    font-weight: 600;
  }

  &.delete-hovered {
    .cell-wrapper {
      background-color: ${colorsV2.red10};
    }
  }
`;

const CardLogicHeaderColors = [
  colorsV2.yellow40,
  colorsV2.primary40,
  colorsV2.purple40,
  colorsV2.green40,
  colorsV2.blue40,
].map((color) => {
  return {
    color,
    key: color,
  };
});

const CardLogicHeaderCellForUser = (props: CardLogicHeaderCellElementProps) => {
  const { element, children } = props;
  const sharedProps = {
    className: cl('header-cell', 'text', element.id),
    style: {
      backgroundColor: element?.backgroundColor,
      width: 'max-content',
      padding: '2px 4px',
      borderRadius: '8px',
    }
  };
  if (element.h3Enabled) {
    return (
      <h3 {...sharedProps}>
        <div className="header-wrapper">{children}</div>
      </h3>
    );
  }

  return (
    <div {...sharedProps}>
      <div className="header-wrapper">{children}</div>
    </div>
  );
};

const CardLogicHeaderCellForAdmin = observer((props: CardLogicHeaderCellElementProps) => {
  const { children, element } = props;
  const cardLogicStore = useCardLogicStore();
  const editor = useSlateStatic();
  const isBackgroundEnabled = element.backgroundColor !== 'transparent';
  return (
    <>
      <Popover
        overlayClassName="no-arrow un-styled"
        align={{
          points: ['tl', 'tl'],
          offset: [6, 0],
          targetOffset: [0, 52],
          overflow: { adjustX: true, adjustY: true },
        }}
        visible={
          cardLogicStore?.currentSelection?.headerCellNodeElementId === element.id &&
          isBackgroundEnabled
        }
        title={
          <ColorSelectionLightStyled>
            <ColorSelector
              showDeselectColor
              {...{ [ELEMENT_BY_ID_ATTRIBUTES]: element.id }}
              colors={CardLogicHeaderColors}
              onDeselectColor={() => {
                CardLogicTransformActions.setNodesByElementId({
                  editor,
                  value: {
                    backgroundColor: 'transparent',
                  },
                  elementId: element.id,
                });
              }}
              onSelectColor={({ color }) => {
                CardLogicTransformActions.setNodesByElementId({
                  editor,
                  value: {
                    backgroundColor: color,
                  },
                  elementId: element.id,
                });
              }}
              currentColor={element?.backgroundColor as string}
              className={`${CARD_LOGIC_HEADER_CELL_COLOR_SELECTION_CLASS_NAME} ${element.id}`}
            />
          </ColorSelectionLightStyled>
        }
      >
        <div
          style={{ minHeight: cardLogicStore?.maxHeaderHeight }}
          className={cl('header-cell', 'text', element.id)}
        >
          <div className="header-wrapper">{children}</div>
        </div>
      </Popover>
      <CardLogicActionMenu element={element} variant="HEADER_CELL" />
    </>
  );
});

export const CardLogicHeaderCellElementEditable = observer(
  (props: CardLogicHeaderCellElementProps) => {
    const { attributes, element } = props;
    const { textAlign, backgroundColor } = element;
    const cardLogicStore = useCardLogicStore();
    return (
      <Main
        {...attributes}
        backgroundColor={backgroundColor}
        {...{ [ELEMENT_BY_ID_ATTRIBUTES]: element.id }}
        className={cl(CARD_LOGIC_HEADER_CELL_CLASS_NAME, element.id, {
          'delete-hovered': cardLogicStore?.deleteButtonHovering,
        })}
      >
        <div className={cl('cell-wrapper', `text-align-${textAlign}`)}>
          <CardLogicHeaderCellForAdmin {...props} />
        </div>
      </Main>
    );
  }
);

export const CardLogicHeaderCellElementReadOnly = (props: CardLogicHeaderCellElementProps) => {
  const { attributes, element } = props;
  const { textAlign, backgroundColor } = element;
  return (
    <Main
      {...attributes}
      backgroundColor={backgroundColor}
      className={cl(CARD_LOGIC_HEADER_CELL_CLASS_NAME, element.id, {})}
    >
      <div className={cl('cell-wrapper', `text-align-${textAlign}`)}>
        <CardLogicHeaderCellForUser {...props} />
      </div>
    </Main>
  );
};
